import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { triageApi } from '../../api/triage';

const TONE = {
  malicious: { bg: 'var(--sev-crit-bg)', fg: 'var(--sev-crit-fg)' },
  suspicious: { bg: 'var(--sev-med-bg)', fg: 'var(--sev-med-fg)' },
  benign: { bg: 'var(--sev-good-bg)', fg: 'var(--sev-good-fg)' },
};

const errorText = (err) => err?.response?.data?.error || err?.message || 'Could not save feedback.';

// Muted one-line text for a verdict that has no result yet (or never will).
// Returns null when compact list rows should stay quiet about it.
const statusText = (verdict, interactive) => {
  switch (verdict.status) {
    case 'queued':
    case 'running':
      return 'AI triage pending…';
    case 'failed':
      return interactive ? 'AI triage failed' : null;
    default: // skipped
      if (!interactive) return null;
      return verdict.reason === 'no_llm' ? 'AI triage skipped: no AI model configured' : 'AI triage skipped';
  }
};

/**
 * AI triage verdict. In list rows (interactive=false) it is a read-only chip
 * (those rows are <button>s, so no interactive controls may live inside).
 * With interactive=true it also shows the reason and, when canRate, thumbs.
 */
const VerdictBadge = ({ verdict, interactive = false, canRate = false, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!verdict) return null;

  if (verdict.status !== 'completed') {
    const text = statusText(verdict, interactive);
    return text ? <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{text}</span> : null;
  }

  const tone = TONE[verdict.verdict] || TONE.suspicious;

  const rate = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await triageApi.sendFeedback(verdict.id, payload);
      if (onChange) onChange(updated);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const thumb = (rating, label, Icon) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={verdict.feedback_rating === rating}
      disabled={busy}
      // Re-clicking the pressed thumb is a no-op: the server replaces feedback, which would erase a corrected label.
      onClick={() => verdict.feedback_rating !== rating && rate({ rating })}
      className="inline-flex items-center justify-center w-6 h-6 rounded-md border"
      style={{
        borderColor: 'var(--surf-line)',
        background: verdict.feedback_rating === rating ? 'var(--surf-raised)' : 'transparent',
        color: verdict.feedback_rating === rating ? 'var(--accent)' : 'var(--ink-muted)',
      }}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <span className={interactive ? 'flex flex-col gap-1.5' : 'inline-flex'}>
      <span className="inline-flex items-center gap-2 flex-wrap">
        <span
          className="inline-flex items-center whitespace-nowrap h-[22px] px-2 text-[10.5px] rounded-md font-mono font-medium tracking-[0.06em]"
          style={{ background: tone.bg, color: tone.fg }}
          title={`AI triage verdict${verdict.rationale ? ': ' + verdict.rationale : ''}`}
        >
          AI · {String(verdict.verdict).toUpperCase()} · {verdict.confidence}
        </span>
        {interactive && canRate && (
          <>
            {thumb('up', 'Agree with AI verdict', ThumbsUp)}
            {thumb('down', 'Disagree with AI verdict', ThumbsDown)}
            {verdict.feedback_rating === 'down' && (
              <label className="text-[11px] inline-flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
                Should have been
                <select
                  value={verdict.feedback_correct_verdict || ''}
                  disabled={busy}
                  onChange={(e) => e.target.value && rate({ rating: 'down', correct_verdict: e.target.value })}
                  className="text-[11px] rounded border px-1 py-0.5"
                  style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-surface)', color: 'var(--ink-text)' }}
                >
                  <option value="">—</option>
                  <option value="benign">benign</option>
                  <option value="suspicious">suspicious</option>
                  <option value="malicious">malicious</option>
                </select>
              </label>
            )}
          </>
        )}
      </span>
      {interactive && verdict.rationale && (
        <span className="text-[12px] leading-snug break-words" style={{ color: 'var(--ink-muted)' }}>{verdict.rationale}</span>
      )}
      {interactive && error && (
        <span role="alert" className="text-[11px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>
      )}
    </span>
  );
};

export default VerdictBadge;
